import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { ILegalRole } from '../../Interfaces/ILegalRole';

/**
 * Health Check für Vorgänge (#1283).
 *
 * Nur `warning` und `error` zählen in die Warnungsanzahl, die beim Speichern im
 * Log landet (#1330). `info` wird angezeigt und nicht gezählt — damit ist die
 * Ausnahme aus dem Ticket ohne Sonderfall abgedeckt: der Hinweis „Mandanten
 * sehen den Vorgang erst im Status Laufend" ist `info`.
 */
export type HealthCheckSeverity = 'info' | 'warning' | 'error';

/** Bereiche, nach denen die Übersicht gruppiert wird (Reihenfolge = Anzeige). */
export const HEALTH_CHECK_SECTIONS = [
  'Stammdaten',
  'Mandanten',
  'Rechnungen',
  'Urkunden',
  'GwG',
  'Phasen',
  'Termine',
  'Vorgangsmanager',
  'Datenerfassung',
  'Identifizierung',
] as const;

export type HealthCheckSection = (typeof HEALTH_CHECK_SECTIONS)[number];

/** Ein einzelner Fund des Health Checks. */
export interface IHealthCheckFinding {
  /** Stabiler Schlüssel der Regel, die den Fund erzeugt hat. */
  ruleKey: string;
  section: HealthCheckSection;
  severity: HealthCheckSeverity;
  /** Fertiger deutscher Text inklusive Namen. */
  message: string;
  /** Sprungziel im Vorgang (`LT_TAB_INDEX`), falls sinnvoll. */
  tabIndex?: number;
}

/**
 * Eine Prüfung, die nicht ausgeführt werden konnte.
 *
 * Wichtig zu unterscheiden von „nichts gefunden": über diesen Punkt ist keine
 * Aussage möglich. Würde das als Fundfreiheit durchgehen, behauptete der Dialog
 * etwas über einen Vorgang, den er insoweit gar nicht angesehen hat.
 */
export interface IHealthCheckSkippedRule {
  ruleKey: string;
  /** Titel der Regel — der Nutzer kennt keine Regelschlüssel. */
  title: string;
  /** Warum sie nicht laufen konnte, in einem Satz. */
  reason: string;
}

/** true, wenn der Fund in die Warnungsanzahl einfließt. */
export const countsAsWarning = (finding: IHealthCheckFinding) =>
  finding.severity !== 'info';

/**
 * Nachschlagedaten, die eine Regel braucht, die aber nicht im Vorgangs-Objekt
 * stehen. Bewusst alles optional: eine Regel, deren Kontext fehlt (weil die
 * Daten noch laden), liefert einfach keinen Fund statt zu werfen.
 */
export interface IHealthCheckContext {
  /** Rollenkatalog des Vorgangs — für „Keine Rolle als Zahlungsschuldner". */
  legalRoles?: ILegalRole[];
  /**
   * Rechtsgeschäftsart im gespeicherten Zustand. Nur damit ist erkennbar, dass
   * sie im Dialog geändert wurde — das Save-Objekt kennt nur den neuen Wert,
   * und `migrateType` wird erst beim Speichern gesetzt.
   */
  savedIdLegalTransactionType?: number;
  /**
   * Anzeigename des aktuell gewählten Status. Aus dem Vorgangs-Objekt wäre nur
   * der Name aus dem Ladezeitpunkt verfügbar, der nach einer Änderung im Dialog
   * nicht mehr zum gewählten Status passt.
   */
  legalTransactionStateName?: string;
}

/** Eingabe des Health Checks: der Vorgang, wie er gespeichert würde. */
export interface IHealthCheckInput {
  legalTransaction: ILegalTransactionFullObject;
  context: IHealthCheckContext;
}

/** Parametertyp einer Regel; wird in den Einstellungen generisch gerendert. */
export interface IHealthCheckRuleParam {
  key: string;
  label: string;
  type: 'number' | 'boolean';
  /** Vorgabe, solange die Kanzlei den Parameter nicht überschreibt. */
  default: number | boolean;
  /** Zusatzhinweis unter dem Eingabefeld. */
  helperText?: string;
}

/** Aufgelöste Parameter einer Regel (Katalog-Default + Kanzlei-Überschreibung). */
export type HealthCheckParams = Record<string, number | boolean>;

/**
 * Eine Regel des Katalogs. Der Katalog liegt bewusst im Code: die fachlich
 * wertvollen Prüfungen sind Aggregationen über die Arrays eines Vorgangs, keine
 * Feldvergleiche.
 */
export interface IHealthCheckRuleDefinition {
  /** Titel für Einstellungen, Vorgangsliste und Log-Text. */
  title: string;
  /** Was die Regel prüft — erklärt dem Admin, ob er sie braucht. */
  description: string;
  section: HealthCheckSection;
  defaultSeverity: HealthCheckSeverity;
  /** Ob die Regel ohne Konfigurationszeile greift. */
  defaultActive: boolean;
  /** Sprungziel im Vorgang für alle Funde dieser Regel. */
  tabIndex?: number;
  params?: IHealthCheckRuleParam[];
  /**
   * Begründung, wenn `evaluate` `null` liefert. Ohne Angabe steht im Dialog ein
   * allgemeiner Satz.
   */
  unavailableReason?: string;
  /**
   * Liefert die Fundtexte. Severity, Bereich und Sprungziel setzt
   * `runHealthCheck` aus der aufgelösten Konfiguration — eine Regel entspricht
   * genau einer Severity, damit sie einzeln abschaltbar ist.
   *
   * `null` heißt „kann ich nicht beurteilen" — etwa weil Nachschlagedaten noch
   * nicht geladen sind. Eine leere Liste heißt dagegen „geprüft, nichts
   * gefunden". Die beiden auseinanderzuhalten ist der einzige Weg, dem grünen
   * Ergebnis trauen zu können.
   */
  evaluate: (
    input: IHealthCheckInput,
    params: HealthCheckParams
  ) => string[] | null;
}

/** Warum eine Regel im aktuellen Vorgang aktiv oder inaktiv ist. */
export type HealthCheckRuleOrigin =
  | 'catalog'
  | 'global'
  | 'scope'
  | 'transaction';

/** Aufgelöster Zustand einer Regel für genau einen Vorgang. */
export interface IHealthCheckRuleState {
  ruleKey: string;
  definition: IHealthCheckRuleDefinition;
  isActive: boolean;
  severity: HealthCheckSeverity;
  params: HealthCheckParams;
  /** Woraus sich `isActive` ergibt — speist die Herkunftsanzeige im Vorgang. */
  origin: HealthCheckRuleOrigin;
  /** Fertiger Herkunftstext, z. B. „für diesen Vorgang von Müller deaktiviert". */
  originLabel: string;
}
